import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuthUser } from "./auth/AuthUserContext";
import MatchVenueMap from "./components/MatchVenueMap";
import FriendSuggestInput from "./components/FriendSuggestInput";
import { SPORTS } from "./sportsOptions";
import {
  getMatches,
  getMatchById,
  bookMatch,
  cancelMatch,
  uploadMatchTeamPhoto,
  deleteMatchTeamPhoto
} from "./api/matchesApi";
import { getMatchChat, postMatchChatMessage } from "./api/matchChatApi";
import {
  getMatchSocialBlocks,
  postMatchSocialBlock,
  deleteMatchSocialBlock,
  getMatchFriends,
  postMatchFriend,
  deleteMatchFriend,
  getIncomingChatRequests,
  postPrivateChatRequest,
  acceptPrivateChatRequest,
  rejectPrivateChatRequest,
  getActivePrivateChats,
  postPrivateChatMessage,
  getPrivateChatMessages
} from "./api/matchSocialApi";
import { sanitizeApiMessage } from "./auth/clientAuth";
import { toSafeMediaUrl } from "./utils/safeMediaUrl";

function hostInitials(name) {
  const s = (name || "?").trim();
  return s.length < 2 ? s.toUpperCase() : (s[0] + s[1]).toUpperCase();
}

function formatMatchDate(isoDate) {
  try {
    const d = typeof isoDate === "string" ? isoDate.split("T")[0] : isoDate;
    return new Date(d).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  } catch {
    return String(isoDate ?? "");
  }
}

function normalizePagedItems(data) {
  if (!data || typeof data !== "object") return [];
  return data.items ?? data.Items ?? [];
}

/** @param {unknown} raw */
function parseApiDateUtc(raw) {
  if (raw == null || raw === "") return null;
  const d = new Date(typeof raw === "string" ? raw : String(raw));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** @param {unknown} raw */
function parseApiDecimal(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).replace(/,/g, "");
  const n = typeof raw === "number" ? raw : parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

/** @param {number | null | undefined} n */
function formatPoolAmount(n) {
  if (n == null || !Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

/** @param {unknown} raw */
function parsePositiveUserId(raw) {
  if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) return raw;
  if (typeof raw === "string" && /^\d+$/.test(raw.trim())) {
    const n = parseInt(raw.trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  return null;
}

/** @param {Record<string, unknown>} m */
function mapApiMatchToCard(m) {
  const hostName = (m.createdBy ?? m.CreatedBy ?? "?").toString();
  const hostUserIdRaw = m.hostUserId ?? m.HostUserId ?? 0;
  const hostUserId = parsePositiveUserId(hostUserIdRaw) ?? 0;
  const players = m.players ?? m.Players ?? [];
  const rawDate = m.date ?? m.Date;
  const dateStr =
    typeof rawDate === "string"
      ? rawDate.split("T")[0]
      : rawDate
        ? new Date(rawDate).toISOString().split("T")[0]
        : "";

  const rosterMap = new Map();
  if (typeof hostUserId === "number" && hostUserId > 0) {
    rosterMap.set(hostUserId, hostName);
  }
  const joinedPlayerIds = [];
  for (const p of players) {
    const name = (p.playerName ?? p.PlayerName ?? "?").toString();
    const uid = parsePositiveUserId(p.userId ?? p.UserId);
    if (uid != null) {
      if (!rosterMap.has(uid)) rosterMap.set(uid, name);
      joinedPlayerIds.push(uid);
    } else {
      const k = `guest:${name}`;
      if (!rosterMap.has(k)) rosterMap.set(k, name);
    }
  }

  const roster = [...rosterMap.entries()].map(([key, displayName]) => ({
    userId: typeof key === "number" ? key : null,
    displayName,
    key: typeof key === "number" ? `u${key}` : String(key)
  }));

  const endTimeUtc = parseApiDateUtc(m.endTime ?? m.EndTime);
  const completed = endTimeUtc != null && endTimeUtc.getTime() <= Date.now();
  const rawTeam = m.teamPhotoUrl ?? m.TeamPhotoUrl;
  const teamPhotoUrl = toSafeMediaUrl(
    typeof rawTeam === "string" ? rawTeam : null
  );

  const slotsOpenForAmount = m.slotsAvailable ?? m.SlotsAvailable ?? 0;
  const slotsBookedForAmount = m.slotsBooked ?? m.SlotsBooked ?? 0;
  const slotCapacity = slotsOpenForAmount + slotsBookedForAmount;
  const totalAmt = parseApiDecimal(m.matchTotalAmount ?? m.MatchTotalAmount) ?? 0;
  let perShare = parseApiDecimal(m.perPersonShare ?? m.PerPersonShare);
  if (perShare == null && totalAmt > 0 && slotCapacity > 0) {
    perShare = Math.round((totalAmt / slotCapacity) * 100) / 100;
  }

  return {
    matchId: m.matchId ?? m.MatchId,
    sport: (m.sport ?? m.Sport ?? "").toString(),
    location: (m.location ?? m.Location ?? "").toString(),
    date: dateStr,
    matchTime: (m.matchTime ?? m.MatchTime ?? "").toString(),
    slotsAvailable: m.slotsAvailable ?? m.SlotsAvailable ?? 0,
    totalPlayers: m.totalPlayers ?? m.TotalPlayers ?? players.length + 1,
    hostName,
    hostUserId,
    joinedPlayerIds,
    roster,
    latitude: m.latitude ?? m.Latitude ?? 0,
    longitude: m.longitude ?? m.Longitude ?? 0,
    endTimeUtc,
    completed,
    teamPhotoUrl,
    matchTotalAmount: totalAmt,
    perPersonShare: perShare
  };
}

function IconPin() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
      />
    </svg>
  );
}

function IconClock() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"
      />
    </svg>
  );
}

function IconPeople() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.67V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"
      />
    </svg>
  );
}

function IconJersey() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 3L4 9v12h16V9l-8-6zm0 2.18l6 4.5V19H6v-9.32l6-4.5zM9 14h6v2H9v-2z"
      />
    </svg>
  );
}

function IconPool() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.37-3.16-1.46-3.27-3.4h1.71c.09.92.74 1.47 1.65 1.47 1.03 0 1.66-.63 1.66-1.47 0-.91-.66-1.45-1.86-1.86l-.63-.22c-1.81-.62-2.65-1.38-2.65-2.85 0-1.45 1.15-2.5 2.86-2.79V6h2.66v1.95c1.6.35 2.72 1.32 2.84 3.05h-1.69c-.11-.86-.68-1.38-1.57-1.38-.93 0-1.56.55-1.56 1.38 0 .78.67 1.22 1.95 1.68l.62.22c1.79.62 2.65 1.45 2.65 2.91 0 1.65-1.23 2.65-3.17 2.95z"
      />
    </svg>
  );
}

function IconChat() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"
      />
    </svg>
  );
}

/** @param {number} matchId */
function buildMatchShareUrl(matchId) {
  if (typeof window === "undefined") return "";
  return `${window.location.origin}/matches?match=${encodeURIComponent(String(matchId))}`;
}

/** @param {{ matchId: number, sport: string, location: string, date: string, matchTime: string }} match */
function formatShareMessage(match) {
  const url = buildMatchShareUrl(match.matchId);
  const when = `${formatMatchDate(match.date)} · ${match.matchTime}`;
  return `Join my ${match.sport} match on WillPlay — ${match.location} (${when})\n${url}`;
}

function FindMatches() {
  const me = useAuthUser();
  const location = useLocation();
  const currentUser = me?.username || "Player";
  const currentUserId = typeof me?.userId === "number" ? me.userId : null;

  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(true);
  const [matchesError, setMatchesError] = useState("");

  const [search, setSearch] = useState("");
  const [sportFilter, setSportFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");

  const [activeGroupChat, setActiveGroupChat] = useState(null);
  const [groupMessageText, setGroupMessageText] = useState("");
  const [groupChats, setGroupChats] = useState({});

  const [blockedUserIds, setBlockedUserIds] = useState([]);
  const [friendsByMatch, setFriendsByMatch] = useState({});
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [activePrivateChats, setActivePrivateChats] = useState([]);
  const [privateChats, setPrivateChats] = useState({});
  const [privateMessageText, setPrivateMessageText] = useState({});
  const [socialBanner, setSocialBanner] = useState(null);
  const [joinFriendByMatch, setJoinFriendByMatch] = useState({});

  const loadMatches = useCallback(async () => {
    setMatchesLoading(true);
    setMatchesError("");
    try {
      const query = {
        pageNumber: 1,
        pageSize: 50,
        ...(search.trim() ? { q: search.trim() } : {}),
        ...(sportFilter ? { sport: sportFilter } : {}),
        ...(dateFilter ? { date: dateFilter } : {}),
        ...(locationFilter.trim() ? { location: locationFilter.trim() } : {})
      };
      const { response, data } = await getMatches(query);
      if (!response.ok) {
        const msg =
          (data && typeof data === "object" && data.message) ||
          (typeof data === "string" && data) ||
          "Could not load matches.";
        setMatchesError(sanitizeApiMessage(String(msg)));
        setMatches([]);
        return;
      }
      const items = normalizePagedItems(data).map(mapApiMatchToCard);
      setMatches(items);
    } catch {
      setMatchesError("Cannot reach the server. Is the API running?");
      setMatches([]);
    } finally {
      setMatchesLoading(false);
    }
  }, [search, sportFilter, dateFilter, locationFilter]);

  const loadSocial = useCallback(async () => {
    if (currentUserId == null) {
      setBlockedUserIds([]);
      setIncomingRequests([]);
      setActivePrivateChats([]);
      setFriendsByMatch({});
      return;
    }
    try {
      const [blocksRes, incomingRes, activeRes] = await Promise.all([
        getMatchSocialBlocks(),
        getIncomingChatRequests(),
        getActivePrivateChats()
      ]);

      if (blocksRes.response.ok && blocksRes.data && typeof blocksRes.data === "object") {
        const ids = blocksRes.data.blockedUserIds ?? blocksRes.data.BlockedUserIds ?? [];
        setBlockedUserIds(Array.isArray(ids) ? ids.map((x) => Number(x)).filter((n) => Number.isFinite(n)) : []);
      }

      if (incomingRes.response.ok && Array.isArray(incomingRes.data)) {
        setIncomingRequests(incomingRes.data);
      } else {
        setIncomingRequests([]);
      }

      if (activeRes.response.ok && Array.isArray(activeRes.data)) {
        setActivePrivateChats(activeRes.data);
        const loads = activeRes.data.map(async (row) => {
          const rid = row.requestId ?? row.RequestId;
          if (rid == null) return;
          const msgRes = await getPrivateChatMessages(rid);
          if (!msgRes.response.ok || !Array.isArray(msgRes.data)) return;
          const mapped = msgRes.data.map((msg) => ({
            sender: msg.senderUsername ?? msg.SenderUsername ?? "?",
            text: msg.message ?? msg.Message ?? "",
            timestamp: new Date(msg.createdAtUtc ?? msg.CreatedAtUtc ?? Date.now()).toLocaleTimeString()
          }));
          setPrivateChats((prev) => ({ ...prev, [rid]: mapped }));
        });
        await Promise.all(loads);
      } else {
        setActivePrivateChats([]);
      }
    } catch {
      /* non-fatal */
    }
  }, [currentUserId]);

  const loadFriendsForMatches = useCallback(
    async (matchList) => {
      if (currentUserId == null || !matchList.length) {
        setFriendsByMatch({});
        return;
      }
      const entries = await Promise.all(
        matchList.map(async (m) => {
          const { response, data } = await getMatchFriends(m.matchId);
          if (!response.ok || !Array.isArray(data)) return [m.matchId, []];
          const ids = data
            .map((row) => row.userId ?? row.UserId)
            .map((x) => Number(x))
            .filter((n) => Number.isFinite(n));
          return [m.matchId, ids];
        })
      );
      const next = {};
      entries.forEach(([id, ids]) => {
        next[id] = ids;
      });
      setFriendsByMatch(next);
    },
    [currentUserId]
  );

  useEffect(() => {
    void loadMatches();
  }, [loadMatches]);

  useEffect(() => {
    void loadSocial();
  }, [loadSocial]);

  useEffect(() => {
    if (matches.length && currentUserId != null) {
      void loadFriendsForMatches(matches);
    }
  }, [matches, currentUserId, loadFriendsForMatches]);

  useEffect(() => {
    if (!socialBanner) return undefined;
    const t = window.setTimeout(() => setSocialBanner(null), 5200);
    return () => window.clearTimeout(t);
  }, [socialBanner]);

  const isBlockedId = (userId) => userId != null && blockedUserIds.includes(userId);

  const isFriendInMatch = (matchId, friendUserId) =>
    (friendsByMatch[matchId] || []).includes(friendUserId);

  const addFriend = async (matchId, friendUserId) => {
    if (friendUserId == null) {
      setSocialBanner("This player is not linked to a user account yet.");
      return;
    }
    if (isBlockedId(friendUserId)) {
      setSocialBanner("Unblock this player before adding them as a friend.");
      return;
    }
    const { response, data } = await postMatchFriend({ matchId, friendUserId });
    if (!response.ok) {
      const msg = (data && data.message) || "Could not add friend.";
      setSocialBanner(sanitizeApiMessage(String(msg)));
      return;
    }
    setSocialBanner("Friend saved for this match.");
    void loadFriendsForMatches(matches);
  };

  const removeFriend = async (matchId, friendUserId) => {
    if (friendUserId == null) return;
    const { response, data } = await deleteMatchFriend({ matchId, friendUserId });
    if (!response.ok) {
      const msg = (data && data.message) || "Could not remove friend.";
      setSocialBanner(sanitizeApiMessage(String(msg)));
      return;
    }
    setSocialBanner("Friend removed.");
    void loadFriendsForMatches(matches);
  };

  const copyMatchLink = async (match) => {
    const url = buildMatchShareUrl(match.matchId);
    try {
      await navigator.clipboard.writeText(url);
      setSocialBanner("Match link copied.");
    } catch {
      setSocialBanner("Could not copy link.");
    }
  };

  const shareViaWhatsApp = (match) => {
    const text = formatShareMessage(match);
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  };

  const shareNative = async (match) => {
    const url = buildMatchShareUrl(match.matchId);
    const text = formatShareMessage(match);
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({ title: "WillPlay match", text, url });
      } catch (e) {
        if (e && typeof e === "object" && "name" in e && e.name !== "AbortError") void copyMatchLink(match);
      }
    } else {
      void copyMatchLink(match);
    }
  };

  const blockUser = async (blockedUserId) => {
    if (blockedUserId == null) {
      setSocialBanner("Cannot block a guest booking without a user id.");
      return;
    }
    const { response, data } = await postMatchSocialBlock({ blockedUserId });
    if (!response.ok) {
      const msg = (data && data.message) || "Could not block user.";
      setSocialBanner(sanitizeApiMessage(String(msg)));
      return;
    }
    setBlockedUserIds((prev) => [...new Set([...prev, blockedUserId])]);
    setSocialBanner("User blocked.");
    void loadSocial();
    void loadFriendsForMatches(matches);
  };

  const unblockUser = async (blockedUserId) => {
    if (blockedUserId == null) return;
    const { response, data } = await deleteMatchSocialBlock(blockedUserId);
    if (!response.ok) {
      const msg = (data && data.message) || "Could not unblock.";
      setSocialBanner(sanitizeApiMessage(String(msg)));
      return;
    }
    setBlockedUserIds((prev) => prev.filter((id) => id !== blockedUserId));
    setSocialBanner("User unblocked.");
    void loadSocial();
  };

  const sendPrivateChatRequest = async (matchId, toUserId) => {
    if (toUserId == null) {
      setSocialBanner("This player cannot receive chat requests yet.");
      return false;
    }
    const { response, data } = await postPrivateChatRequest({ matchId, toUserId });
    if (!response.ok) {
      const msg = (data && data.message) || "Could not send request.";
      setSocialBanner(sanitizeApiMessage(String(msg)));
      return false;
    }
    setSocialBanner("Private chat request sent.");
    void loadSocial();
    return true;
  };

  const acceptIncoming = async (requestId) => {
    const { response, data } = await acceptPrivateChatRequest(requestId);
    if (!response.ok) {
      const msg = (data && data.message) || "Could not accept.";
      setSocialBanner(sanitizeApiMessage(String(msg)));
      return;
    }
    setSocialBanner("Chat accepted.");
    void loadSocial();
  };

  const rejectIncoming = async (requestId) => {
    const { response, data } = await rejectPrivateChatRequest(requestId);
    if (!response.ok) {
      const msg = (data && data.message) || "Could not reject.";
      setSocialBanner(sanitizeApiMessage(String(msg)));
      return;
    }
    setSocialBanner("Request declined.");
    void loadSocial();
  };

  const sendPrivateMessage = async (requestId) => {
    const text = (privateMessageText[requestId] || "").trim();
    if (!text) return;
    const { response, data } = await postPrivateChatMessage(requestId, { message: text });
    if (!response.ok) {
      const msg = (data && data.message) || "Could not send message.";
      setSocialBanner(sanitizeApiMessage(String(msg)));
      return;
    }
    setPrivateMessageText((prev) => ({ ...prev, [requestId]: "" }));
    const msgRes = await getPrivateChatMessages(requestId);
    if (msgRes.response.ok && Array.isArray(msgRes.data)) {
      const mapped = msgRes.data.map((msg) => ({
        sender: msg.senderUsername ?? msg.SenderUsername ?? "?",
        text: msg.message ?? msg.Message ?? "",
        timestamp: new Date(msg.createdAtUtc ?? msg.CreatedAtUtc ?? Date.now()).toLocaleTimeString()
      }));
      setPrivateChats((prev) => ({ ...prev, [requestId]: mapped }));
    }
  };

  const loadGroupThread = async (matchId) => {
    const { response, data } = await getMatchChat(matchId);
    if (response.status === 403 && data && typeof data === "object" && data.code === "match_chat_ended") {
      setGroupChats((prev) => ({ ...prev, [matchId]: [] }));
      setSocialBanner("This match has ended — group chat is closed.");
      return;
    }
    if (!response.ok || !Array.isArray(data)) {
      setGroupChats((prev) => ({ ...prev, [matchId]: [] }));
      return;
    }
    const mapped = data.map((msg) => ({
      sender: msg.senderUsername ?? msg.SenderUsername ?? "?",
      text: msg.message ?? msg.Message ?? "",
      timestamp: new Date(
        msg.createdAt ?? msg.CreatedAt ?? msg.createdAtUtc ?? msg.CreatedAtUtc ?? Date.now()
      ).toLocaleTimeString()
    }));
    setGroupChats((prev) => ({ ...prev, [matchId]: mapped }));
  };

  const sendGroupMessage = async () => {
    if (!activeGroupChat || !groupMessageText.trim()) return;
    const { response, data } = await postMatchChatMessage(activeGroupChat, groupMessageText.trim());
    if (!response.ok) {
      const msg = (data && data.message) || "Could not send.";
      setSocialBanner(sanitizeApiMessage(String(msg)));
      return;
    }
    setGroupMessageText("");
    await loadGroupThread(activeGroupChat);
  };

  const handleJoinMatch = async (matchId) => {
    if (currentUserId == null) {
      setSocialBanner("Sign in to join a match.");
      return;
    }
    const friendName = (joinFriendByMatch[matchId] ?? "").trim();
    const { response, data } = await bookMatch(matchId, { friendName });
    if (!response.ok) {
      const msg = (data && data.message) || "Could not join.";
      setSocialBanner(sanitizeApiMessage(String(msg)));
      return;
    }
    setJoinFriendByMatch((prev) => {
      const next = { ...prev };
      delete next[matchId];
      return next;
    });
    setSocialBanner("You joined this match.");
    await loadMatches();
    void loadSocial();
  };

  const handleLeaveMatch = async (matchId) => {
    if (currentUserId == null) {
      setSocialBanner("Sign in to manage your booking.");
      return;
    }
    if (!window.confirm("Leave this match? Your slot will be released.")) return;

    const { response, data } = await cancelMatch(matchId);
    if (!response.ok) {
      const msg = (data && data.message) || "Could not cancel booking.";
      setSocialBanner(sanitizeApiMessage(String(msg)));
      return;
    }
    setSocialBanner("You left this match.");
    await loadMatches();
    void loadSocial();
  };

  const [teamPhotoBusy, setTeamPhotoBusy] = useState(null);
  const [teamPhotoError, setTeamPhotoError] = useState("");

  const handleTeamPhotoFile = useCallback(
    async (e, matchId) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      setTeamPhotoError("");
      setTeamPhotoBusy(matchId);
      try {
        const { response, data } = await uploadMatchTeamPhoto(matchId, file);
        if (!response.ok) {
          const raw =
            (data && typeof data === "object" && typeof data.message === "string" && data.message) ||
            (typeof data === "string" && data) ||
            "Upload failed.";
          setTeamPhotoError(sanitizeApiMessage(String(raw)));
          return;
        }
        setSocialBanner("Team photo saved.");
        await loadMatches();
      } catch {
        setTeamPhotoError("Could not upload. Check your connection.");
      } finally {
        setTeamPhotoBusy(null);
      }
    },
    [loadMatches]
  );

  const handleDeleteTeamPhoto = useCallback(
    async (matchId) => {
      setTeamPhotoError("");
      setTeamPhotoBusy(matchId);
      try {
        const { response, data } = await deleteMatchTeamPhoto(matchId);
        if (!response.ok) {
          const raw =
            (data && typeof data === "object" && typeof data.message === "string" && data.message) ||
            (typeof data === "string" && data) ||
            "Could not remove.";
          setTeamPhotoError(sanitizeApiMessage(String(raw)));
          return;
        }
        setSocialBanner("Team photo removed.");
        await loadMatches();
      } catch {
        setTeamPhotoError("Could not remove photo.");
      } finally {
        setTeamPhotoBusy(null);
      }
    },
    [loadMatches]
  );

  const openGroupChat = (matchId) => {
    setActiveGroupChat(matchId);
    void loadGroupThread(matchId);
  };

  useEffect(() => {
    if (activeGroupChat == null) return;
    const row = matches.find((x) => x.matchId === activeGroupChat);
    if (row?.completed) {
      setActiveGroupChat(null);
      setGroupMessageText("");
    }
  }, [matches, activeGroupChat]);

  const filteredMatches = useMemo(() => matches, [matches]);

  const matchQueryId = useMemo(() => new URLSearchParams(location.search).get("match"), [location.search]);

  const displayList = useMemo(() => {
    const filtered = filteredMatches.filter((match) => {
      const values = `${match.sport} ${match.location}`.toLowerCase();
      const searchHit = values.includes(search.toLowerCase());
      const sportHit = sportFilter ? match.sport === sportFilter : true;
      const dateHit = dateFilter ? match.date === dateFilter : true;
      const locationHit = locationFilter
        ? match.location.toLowerCase().includes(locationFilter.toLowerCase())
        : true;
      return searchHit && sportHit && dateHit && locationHit;
    });

    if (!matchQueryId) return filtered;

    const shared = filteredMatches.find((m) => String(m.matchId) === matchQueryId);
    if (!shared || filtered.some((m) => m.matchId === shared.matchId)) return filtered;

    return [shared, ...filtered];
  }, [filteredMatches, search, sportFilter, dateFilter, locationFilter, matchQueryId]);

  useEffect(() => {
    if (!matchQueryId || matchesLoading) return undefined;
    const exists = matches.some((m) => String(m.matchId) === matchQueryId);
    if (exists) return undefined;

    let cancelled = false;
    void (async () => {
      const id = parseInt(matchQueryId, 10);
      if (!Number.isFinite(id)) return;
      try {
        const { response, data } = await getMatchById(id);
        if (cancelled || !response.ok || !data) return;
        const card = mapApiMatchToCard(data);
        setMatches((prev) => (prev.some((m) => m.matchId === card.matchId) ? prev : [...prev, card]));
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [matchQueryId, matchesLoading, matches]);

  useEffect(() => {
    if (!matchQueryId) return undefined;
    const el = document.getElementById(`match-card-${matchQueryId}`);
    if (!el) return undefined;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("match-card--highlight");
    const clear = window.setTimeout(() => el.classList.remove("match-card--highlight"), 2600);
    return () => {
      window.clearTimeout(clear);
      el.classList.remove("match-card--highlight");
    };
  }, [matchQueryId, displayList]);

  const openMatches = useMemo(() => displayList.filter((m) => !m.completed), [displayList]);
  const completedMatches = useMemo(() => displayList.filter((m) => m.completed), [displayList]);

  const isMember = (match) => {
    if (currentUserId == null) return false;
    if (match.hostUserId === currentUserId) return true;
    return match.joinedPlayerIds.includes(currentUserId);
  };

  const renderMatchCard = (match) => {
    const member = isMember(match);
    const groupMessages = groupChats[match.matchId] || [];
    const friendIdsHere = friendsByMatch[match.matchId] || [];
    const friendsHere = friendIdsHere
      .map((fid) => match.roster.find((r) => r.userId === fid)?.displayName)
      .filter(Boolean);
    const slotsOpen = match.slotsAvailable;
    const slotsLabel = match.completed
      ? "Completed"
      : slotsOpen <= 0
        ? "Full"
        : slotsOpen === 1
          ? "1 spot left"
          : `${slotsOpen} spots open`;

    return (
            <article
              id={`match-card-${match.matchId}`}
              className={`match-card${match.completed ? " match-card--completed" : ""}`}
              key={match.matchId}
            >
              <header className="match-card__head">
                <div className="match-card__head-main">
                  <h3 className="match-card__sport">{match.sport}</h3>
                  <p className="match-card__host">
                    <span className="match-card__avatar" aria-hidden="true">
                      {hostInitials(match.hostName)}
                    </span>
                    <span className="match-card__host-text">
                      Hosted by <strong>{match.hostName}</strong>
                    </span>
                  </p>
                </div>
                <span
                  className={`match-card__chip${
                    match.completed ? " match-card__chip--completed" : slotsOpen <= 0 ? " match-card__chip--full" : ""
                  }`}
                  title={match.completed ? "Completed" : "Open slots"}
                >
                  {slotsLabel}
                </span>
              </header>

              {!match.completed ? (
                <div className="match-card__share" aria-label="Share this match">
                  <button type="button" className="ghost-btn small" onClick={() => void copyMatchLink(match)}>
                    Copy link
                  </button>
                  <button type="button" className="ghost-btn small" onClick={() => shareViaWhatsApp(match)}>
                    WhatsApp
                  </button>
                  {typeof navigator !== "undefined" && typeof navigator.share === "function" ? (
                    <button type="button" className="ghost-btn small" onClick={() => void shareNative(match)}>
                      Share…
                    </button>
                  ) : null}
                </div>
              ) : null}

              <ul className="match-card__meta" aria-label="Match details">
                <li>
                  <span className="match-card__meta-icon" aria-hidden="true">
                    <IconPin />
                  </span>
                  <span className="match-card__meta-body">
                    <span className="match-card__meta-label">Venue</span>
                    <span className="match-card__meta-value">{match.location}</span>
                  </span>
                </li>
                <li>
                  <span className="match-card__meta-icon" aria-hidden="true">
                    <IconClock />
                  </span>
                  <span className="match-card__meta-body">
                    <span className="match-card__meta-label">Schedule</span>
                    <span className="match-card__meta-value">
                      {formatMatchDate(match.date)} · {match.matchTime}
                    </span>
                  </span>
                </li>
                <li>
                  <span className="match-card__meta-icon" aria-hidden="true">
                    <IconPeople />
                  </span>
                  <span className="match-card__meta-body">
                    <span className="match-card__meta-label">Players</span>
                    <span className="match-card__meta-value">
                      {match.joinedPlayerIds.length} booked
                      {match.totalPlayers ? ` · Roster cap ${match.totalPlayers}` : ""}
                    </span>
                  </span>
                </li>
                <li>
                  <span className="match-card__meta-icon" aria-hidden="true">
                    <IconJersey />
                  </span>
                  <span className="match-card__meta-body">
                    <span className="match-card__meta-label">Capacity</span>
                    <span className="match-card__meta-value">Up to {match.totalPlayers} players</span>
                  </span>
                </li>
                {match.matchTotalAmount > 0 && match.perPersonShare != null ? (
                  <li>
                    <span className="match-card__meta-icon" aria-hidden="true">
                      <IconPool />
                    </span>
                    <span className="match-card__meta-body">
                      <span className="match-card__meta-label">Match pool</span>
                      <span className="match-card__meta-value">
                        {formatPoolAmount(match.matchTotalAmount)} total · {formatPoolAmount(match.perPersonShare)} per
                        player slot
                      </span>
                    </span>
                  </li>
                ) : null}
              </ul>

              {typeof match.latitude === "number" &&
              typeof match.longitude === "number" &&
              !(match.latitude === 0 && match.longitude === 0) ? (
                <div className="match-card__map-row">
                  <MatchVenueMap
                    latitude={match.latitude}
                    longitude={match.longitude}
                    title={match.location}
                  />
                  <a
                    className="match-card__maps-link"
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${match.latitude},${match.longitude}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open in Google Maps
                  </a>
                </div>
              ) : null}

              {match.completed ? (
                <p className="match-card__status match-card__status--done">
                  Match ended — group chat is closed.
                </p>
              ) : null}

              {match.completed && match.teamPhotoUrl ? (
                <div className="match-card__team-photo">
                  <img src={match.teamPhotoUrl} alt="" className="match-card__team-img" />
                </div>
              ) : null}

              {match.completed &&
              currentUserId != null &&
              match.hostUserId === currentUserId ? (
                <div className="match-card__team-upload">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="visually-hidden-input"
                    aria-label="Upload team photo"
                    disabled={teamPhotoBusy === match.matchId}
                    onChange={(e) => void handleTeamPhotoFile(e, match.matchId)}
                    id={`team-photo-${match.matchId}`}
                  />
                  <div className="match-card__team-upload-actions">
                    <button
                      type="button"
                      className="primary-btn small"
                      disabled={teamPhotoBusy === match.matchId}
                      onClick={() => document.getElementById(`team-photo-${match.matchId}`)?.click()}
                    >
                      {teamPhotoBusy === match.matchId
                        ? "Working…"
                        : match.teamPhotoUrl
                          ? "Change team photo"
                          : "Upload team photo"}
                    </button>
                    {match.teamPhotoUrl ? (
                      <button
                        type="button"
                        className="ghost-btn small"
                        disabled={teamPhotoBusy === match.matchId}
                        onClick={() => void handleDeleteTeamPhoto(match.matchId)}
                      >
                        Remove team photo
                      </button>
                    ) : null}
                  </div>
                  <p className="muted small match-card__team-hint">Optional team photo from this match</p>
                </div>
              ) : null}

              {!match.completed && currentUserId != null && !member && slotsOpen > 0 ? (
                <div className="match-card__join-extras">
                  {match.matchTotalAmount > 0 && match.perPersonShare != null ? (
                    <p className="match-card__amount-estimate">
                      <strong>Estimated share:</strong> {formatPoolAmount(match.perPersonShare)} if you join solo
                      {slotsOpen >= 2 ? (
                        <>
                          {" "}
                          · {formatPoolAmount(match.perPersonShare * 2)} if you bring a friend (2 slots)
                        </>
                      ) : (
                        <span className="muted"> · Only one spot open — solo join only</span>
                      )}
                    </p>
                  ) : null}
                  {slotsOpen >= 2 ? (
                    <label className="match-card__friend-field">
                      Optional friend (uses 2 spots)
                      <p className="muted small" style={{ margin: "0 0 4px", fontWeight: 500 }}>
                        Search all WillPlay users or type a name for the guest slot.
                      </p>
                      <FriendSuggestInput
                        id={`join-friend-${match.matchId}`}
                        value={joinFriendByMatch[match.matchId] ?? ""}
                        onChange={(next) =>
                          setJoinFriendByMatch((prev) => ({
                            ...prev,
                            [match.matchId]: next
                          }))
                        }
                        placeholder="Type to search users…"
                      />
                    </label>
                  ) : null}
                </div>
              ) : null}

              <footer className="match-card__footer">
                {!match.completed ? (
                  <>
                    <button
                      type="button"
                      className={`primary-btn match-card__join${member ? " match-card__join--joined" : ""}`}
                      disabled={member || currentUserId == null}
                      onClick={() => handleJoinMatch(match.matchId)}
                    >
                      {member
                        ? "You are in this match"
                        : currentUserId == null
                          ? "Sign in to join"
                          : "Join match"}
                    </button>
                    {member &&
                    currentUserId != null &&
                    match.hostUserId !== currentUserId ? (
                      <button
                        type="button"
                        className="ghost-btn small match-card__leave"
                        onClick={() => void handleLeaveMatch(match.matchId)}
                      >
                        Leave match
                      </button>
                    ) : null}
                  </>
                ) : null}

                {member && !match.completed && currentUserId != null ? (
                  <div className="match-card__squad" aria-label="Squad and social">
                    <h4 className="match-card__squad-title">Squad &amp; social</h4>
                    <p className="match-card__squad-hint">
                      Friends, private chat requests, and blocks are saved to your WillPlay account.
                    </p>
                    {friendsHere.length > 0 ? (
                      <p className="match-card__squad-friends">
                        <strong>Match friends:</strong> {friendsHere.join(", ")}
                      </p>
                    ) : null}
                    {match.roster.map((row) => {
                      const uid = row.userId;
                      const isSelf = uid != null && uid === currentUserId;
                      const blocked = uid != null && isBlockedId(uid);
                      if (isSelf) {
                        return (
                          <div className="match-card__squad-row" key={`${match.matchId}-${row.key}`}>
                            <span className="match-card__squad-name match-card__squad-name--self">You</span>
                            <span className="muted" style={{ fontSize: "0.8rem" }}>
                              {currentUser}
                            </span>
                          </div>
                        );
                      }
                      if (blocked) {
                        return (
                          <div className="match-card__squad-row" key={`${match.matchId}-${row.key}-blocked`}>
                            <span className="match-card__squad-name">{row.displayName}</span>
                            <div className="match-card__squad-actions">
                              <span className="muted" style={{ fontSize: "0.78rem" }}>
                                Blocked
                              </span>
                              <button
                                type="button"
                                className="ghost-btn small"
                                onClick={() => unblockUser(uid)}
                                disabled={uid == null}
                              >
                                Unblock
                              </button>
                            </div>
                          </div>
                        );
                      }
                      const friended = uid != null && isFriendInMatch(match.matchId, uid);
                      return (
                        <div className="match-card__squad-row" key={`${match.matchId}-${row.key}`}>
                          <span className="match-card__squad-name">{row.displayName}</span>
                          <div className="match-card__squad-actions">
                            {friended ? (
                              <button
                                type="button"
                                className="ghost-btn small"
                                onClick={() => removeFriend(match.matchId, uid)}
                                disabled={uid == null}
                              >
                                Remove friend
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="primary-btn small"
                                onClick={() => addFriend(match.matchId, uid)}
                                disabled={uid == null}
                              >
                                Add friend
                              </button>
                            )}
                            <button
                              type="button"
                              className="ghost-btn small"
                              onClick={() => sendPrivateChatRequest(match.matchId, uid)}
                              disabled={uid == null}
                            >
                              Private chat
                            </button>
                            <button
                              type="button"
                              className="ghost-btn small"
                              onClick={() => blockUser(uid)}
                              disabled={uid == null}
                            >
                              Block
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                {member && !match.completed ? (
                  <div className="match-card__actions">
                    <button
                      type="button"
                      className="ghost-btn small match-card__action-btn"
                      onClick={() => openGroupChat(match.matchId)}
                    >
                      <span className="match-card__action-icon" aria-hidden="true">
                        <IconChat />
                      </span>
                      Group chat
                    </button>
                  </div>
                ) : null}
              </footer>

              {activeGroupChat === match.matchId && !match.completed ? (
                <div className="chat-box match-card__chat">
                  <h4 className="match-card__chat-title">
                    Group chat — {match.roster.length} member{match.roster.length === 1 ? "" : "s"} (host + players)
                  </h4>
                  <div className="chat-feed">
                    {groupMessages.length === 0 ? <p>No messages yet.</p> : null}
                    {groupMessages.map((msg, idx) => (
                      <p key={`${msg.timestamp}-${idx}`}>
                        <strong>{msg.sender}:</strong> {msg.text}{" "}
                        <span className="chat-time">{msg.timestamp}</span>
                      </p>
                    ))}
                  </div>
                  <div className="chat-input-row">
                    <input
                      value={groupMessageText}
                      onChange={(e) => setGroupMessageText(e.target.value)}
                      placeholder="Type a message..."
                    />
                    <button type="button" className="primary-btn small" onClick={() => void sendGroupMessage()}>
                      Send
                    </button>
                  </div>
                </div>
              ) : null}
            </article>
    );
  };

  return (
    <main className="page">
      <section className="panel">
        <div className="find-matches__head">
          <h1>Match Listing</h1>
          <div className="find-matches__head-actions">
            <Link to="/create-match" className="primary-btn small">
              Host a match
            </Link>
            <Link to="/chat" className="ghost-btn small find-matches__chat-link">
              Messages / chat
            </Link>
          </div>
        </div>
        <div className="filters">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by sport or location"
          />
          <select value={sportFilter} onChange={(e) => setSportFilter(e.target.value)}>
            <option value="">All Sports</option>
            {SPORTS.map((sport) => (
              <option key={sport} value={sport}>
                {sport}
              </option>
            ))}
          </select>
          <input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} />
          <input
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            placeholder="Filter by location"
          />
        </div>
      </section>

      {matchesError ? (
        <section className="panel" role="alert">
          <p className="error-text">{matchesError}</p>
        </section>
      ) : null}

      {socialBanner ? (
        <section className="panel find-matches__banner" role="status">
          <p>{socialBanner}</p>
        </section>
      ) : null}

      {matchesLoading ? (
        <section className="panel">
          <p className="muted">Loading matches…</p>
        </section>
      ) : null}

      {!matchesLoading && !displayList.length ? (
        <section className="panel">
          <p className="muted">No matches match your filters. Try clearing filters or create a match.</p>
        </section>
      ) : null}

      {teamPhotoError ? (
        <section className="panel" role="alert">
          <p className="error-text">{teamPhotoError}</p>
        </section>
      ) : null}

      {!matchesLoading && openMatches.length === 0 && completedMatches.length > 0 && displayList.length > 0 ? (
        <section className="panel">
          <p className="muted">No open matches for these filters. See completed matches below.</p>
        </section>
      ) : null}

      {openMatches.length > 0 ? (
        <section className="panel find-matches__section-intro">
          <h2 className="find-matches__section-title">Open matches</h2>
        </section>
      ) : null}

      {openMatches.length > 0 ? (
        <section className="card-grid match-card-grid">
          {openMatches.map(renderMatchCard)}
        </section>
      ) : null}

      {completedMatches.length > 0 ? (
        <>
          <section className="panel completed-matches-panel">
            <h2 className="find-matches__section-title">Completed matches</h2>
            <p className="muted small">Past matches. Hosts can add a team photo.</p>
          </section>
          <section className="card-grid match-card-grid match-card-grid--completed">
            {completedMatches.map(renderMatchCard)}
          </section>
        </>
      ) : null}

      {incomingRequests.length > 0 ? (
        <section className="panel">
          <h3>Chat Requests For You</h3>
          {incomingRequests.map((request) => {
            const id = request.id ?? request.Id;
            const from = request.fromUsername ?? request.FromUsername ?? "?";
            const mid = request.matchId ?? request.MatchId;
            return (
              <div className="request-row" key={id}>
                <span>
                  {from} requested private chat (Match #{mid})
                </span>
                <div className="request-row__actions">
                  <button type="button" className="primary-btn small" onClick={() => void acceptIncoming(id)}>
                    Accept
                  </button>
                  <button type="button" className="ghost-btn small" onClick={() => void rejectIncoming(id)}>
                    Reject
                  </button>
                </div>
              </div>
            );
          })}
        </section>
      ) : null}

      {activePrivateChats.length > 0 ? (
        <section className="panel">
          <h3>Private Chats</h3>
          {activePrivateChats.map((chat) => {
            const requestId = chat.requestId ?? chat.RequestId;
            const title = chat.otherUsername ?? chat.OtherUsername ?? "Chat";
            return (
              <div className="chat-box" key={requestId}>
                <h4>
                  {title} · Match #{chat.matchId ?? chat.MatchId}
                </h4>
                <div className="chat-feed">
                  {(privateChats[requestId] || []).length === 0 ? <p>No messages yet.</p> : null}
                  {(privateChats[requestId] || []).map((msg, idx) => (
                    <p key={`${msg.timestamp}-${idx}`}>
                      <strong>{msg.sender}:</strong> {msg.text}{" "}
                      <span className="chat-time">{msg.timestamp}</span>
                    </p>
                  ))}
                </div>
                <div className="chat-input-row">
                  <input
                    value={privateMessageText[requestId] || ""}
                    onChange={(e) =>
                      setPrivateMessageText((prev) => ({ ...prev, [requestId]: e.target.value }))
                    }
                    placeholder="Type private message"
                  />
                  <button type="button" className="primary-btn small" onClick={() => void sendPrivateMessage(requestId)}>
                    Send
                  </button>
                </div>
              </div>
            );
          })}
        </section>
      ) : null}
    </main>
  );
}

export default FindMatches;
